// BUILD-046 task 9.39 (found by the browser run): the confirmation of an ACCEPTED oversight or owner action must survive the list reload that follows.
//
// After Reassign…, Return to queue, Recovery assignment… or Reopen is accepted, the host reloads the lists the change touched. A reload shows the
// list's loading state first (listStateModel: a list that is being requested is drawn as loading, never as stale rows), so the row that carried the
// action controls is removed and drawn again, and its own "Returned to the queue." / "Reassigned to <name>." message went with it: the Caller saw
// the row change and no words. This small memory keeps the accepted outcome text per task id for the life of the page session, so the row that is
// drawn again shows it until the Caller dismisses it, starts another action on that task, or the Caller changes (the previous Caller's messages
// are never shown to the next one). It holds text only; it applies nothing and decides nothing.
//
// A second case from the same run: some accepted actions move the task OUT of the list the Caller is looking at (a manager reassigns a report's
// task to someone who is not her report; an owner returns her claimed task to the queue). The row is not drawn again, so its message would never
// be seen. `notices(presentIds)` names the remembered outcomes whose task is not in the list that has finished loading, so the page can show
// them in a place of their own (ActionOutcomeNotices.tsx) until they are dismissed.
//
// Framework-free; erasable TypeScript.

export interface OutcomeNotice {
  taskId: string;
  /** The task title as the row showed it ('' when it had none). */
  title: string;
  text: string;
}

export interface OutcomeMemory {
  /** Keep the accepted outcome text for a task. Newer entries replace older ones; the oldest go first when the memory is full. */
  remember: (taskId: string, text: string, title?: string, scope?: string) => void;
  /** The remembered text for a task, or null. */
  recall: (taskId: string) => string | null;
  forget: (taskId: string) => void;
  /** A Caller change: nothing the previous Caller was told is kept. */
  clear: () => void;
  size: () => number;
  /**
   * The remembered outcomes whose task is NOT among `presentIds`, oldest first; with a `scope`, only those taken in that view. `null` means the
   * list has not finished loading (or failed): nothing can be said about which tasks it holds, so no notice is returned (a row that is about to
   * be drawn again must not also appear as a notice).
   */
  notices: (presentIds: readonly string[] | null, scope?: string) => OutcomeNotice[];
  /** Called after every change to the memory; returns the unsubscribe function. */
  subscribe: (listener: () => void) => () => void;
  /** Changes whenever the memory changes (the snapshot a React store reads). */
  version: () => number;
}

export const OUTCOME_MEMORY_LIMIT = 50;

/**
 * The task ids a list state holds, for `notices`: the ids when the list has data or is empty (it has finished loading and an empty list holds
 * nothing), and null while it is loading or failed (nothing can be said about what it holds).
 */
export function presentIdsOf(state: { phase: string; items: readonly unknown[] } | null | undefined): string[] | null {
  if (state === null || state === undefined || (state.phase !== 'data' && state.phase !== 'empty')) return null;
  const ids: string[] = [];
  for (const item of state.items) {
    const id = item !== null && typeof item === 'object' ? (item as { id?: unknown }).id : undefined;
    if (typeof id === 'string') ids.push(id);
  }
  return ids;
}

interface Entry {
  text: string;
  title: string;
  /** The view the action was taken in ('oversight', 'my-tasks', ...): a notice is shown only by the view that took the action. */
  scope: string;
}

export function createOutcomeMemory(limit: number = OUTCOME_MEMORY_LIMIT): OutcomeMemory {
  const entries = new Map<string, Entry>();
  const listeners = new Set<() => void>();
  let version = 0;
  const changed = (): void => {
    version += 1;
    for (const listener of [...listeners]) listener();
  };
  return {
    remember(taskId, text, title = '', scope = '') {
      if (typeof taskId !== 'string' || taskId === '' || typeof text !== 'string' || text.trim() === '') return;
      entries.delete(taskId);
      entries.set(taskId, { text, title: typeof title === 'string' ? title : '', scope: typeof scope === 'string' ? scope : '' });
      while (entries.size > limit) {
        const oldest = entries.keys().next();
        if (oldest.done === true) break;
        entries.delete(oldest.value);
      }
      changed();
    },
    recall(taskId) {
      return entries.get(taskId)?.text ?? null;
    },
    forget(taskId) {
      if (entries.delete(taskId)) changed();
    },
    clear() {
      if (entries.size === 0) return;
      entries.clear();
      changed();
    },
    size: () => entries.size,
    notices(presentIds, scope) {
      if (presentIds === null) return [];
      const present = new Set(presentIds);
      const out: OutcomeNotice[] = [];
      for (const [taskId, entry] of entries) {
        if (present.has(taskId) || (scope !== undefined && entry.scope !== scope)) continue;
        out.push({ taskId, title: entry.title, text: entry.text });
      }
      return out;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    version: () => version,
  };
}
